// ── Production Status Types & Config ─────────────────────────────────────────

export type ProductionPhase = 'onboarding' | 'development' | 'qa' | 'launch'

export type ProductionStatus =
  | 'not-started'
  | 'proposal-sent'
  | 'spec-confirmed'
  | 'in-production'
  | 'integrated'
  | 'internal-qa'
  | 'builder-review'
  | 'builder-feedback'
  | 'live'

export type ProductionStageConfig = {
  key: ProductionStatus
  label: string
  shortLabel: string
  subtitle?: string
  phase: ProductionPhase
  color: string
  bgColor: string
  borderColor: string
  dotColor: string
  order: number
  qaLevel?: number
}

export const productionPhaseConfig: Record<
  ProductionPhase,
  { label: string; color: string; bgColor: string }
> = {
  onboarding: {
    label: 'Onboarding',
    color: 'text-slate-700',
    bgColor: 'bg-slate-100',
  },
  development: {
    label: 'Development',
    color: 'text-blue-700',
    bgColor: 'bg-blue-50',
  },
  qa: {
    label: 'QA',
    color: 'text-violet-700',
    bgColor: 'bg-violet-50',
  },
  launch: {
    label: 'Launch',
    color: 'text-emerald-700',
    bgColor: 'bg-emerald-50',
  },
}

export const productionStages: ProductionStageConfig[] = [
  // ── Onboarding ──
  {
    key: 'not-started',
    label: 'Not Started',
    shortLabel: 'Not Started',
    phase: 'onboarding',
    color: 'text-slate-600',
    bgColor: 'bg-slate-100',
    borderColor: 'border-slate-200',
    dotColor: 'bg-slate-400',
    order: 0,
  },
  {
    key: 'proposal-sent',
    label: 'Pending',
    shortLabel: 'Pending',
    subtitle: 'Proposal Sent',
    phase: 'onboarding',
    color: 'text-slate-700',
    bgColor: 'bg-slate-100',
    borderColor: 'border-slate-300',
    dotColor: 'bg-slate-500',
    order: 1,
  },
  {
    key: 'spec-confirmed',
    label: 'Approved',
    shortLabel: 'Approved',
    subtitle: 'Spec Confirmed',
    phase: 'onboarding',
    color: 'text-slate-800',
    bgColor: 'bg-slate-200',
    borderColor: 'border-slate-300',
    dotColor: 'bg-slate-600',
    order: 2,
  },
  // ── Development ──
  {
    key: 'in-production',
    label: 'In Production',
    shortLabel: 'In Prod',
    subtitle: 'In Dev',
    phase: 'development',
    color: 'text-blue-700',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    dotColor: 'bg-blue-500',
    order: 3,
  },
  {
    key: 'integrated',
    label: 'Complete',
    shortLabel: 'Complete',
    subtitle: 'Integrated',
    phase: 'development',
    color: 'text-blue-800',
    bgColor: 'bg-blue-100',
    borderColor: 'border-blue-300',
    dotColor: 'bg-blue-600',
    order: 4,
  },
  // ── QA ──
  {
    key: 'internal-qa',
    label: 'Internal QA',
    shortLabel: 'QA',
    subtitle: 'QA — progresses as fixes are completed and new builds released',
    phase: 'qa',
    color: 'text-violet-700',
    bgColor: 'bg-violet-50',
    borderColor: 'border-violet-200',
    dotColor: 'bg-violet-500',
    order: 5,
    qaLevel: 1,
  },
  // ── Launch ──
  {
    key: 'builder-review',
    label: 'Builder Review',
    shortLabel: 'Builder Review',
    subtitle: 'Soft Launch',
    phase: 'launch',
    color: 'text-cyan-700',
    bgColor: 'bg-cyan-50',
    borderColor: 'border-cyan-200',
    dotColor: 'bg-cyan-500',
    order: 6,
  },
  {
    key: 'builder-feedback',
    label: 'Builder Feedback',
    shortLabel: 'Builder Feedback',
    subtitle: 'In Dev — Fixes',
    phase: 'launch',
    color: 'text-orange-700',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
    dotColor: 'bg-orange-500',
    order: 7,
  },
  {
    key: 'live',
    label: 'Live',
    shortLabel: 'Live',
    subtitle: 'Official Launch',
    phase: 'launch',
    color: 'text-emerald-700',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
    dotColor: 'bg-emerald-500',
    order: 8,
  },
]

export const getStageConfig = (
  status: ProductionStatus,
): ProductionStageConfig =>
  productionStages.find((s) => s.key === status) || productionStages[0]

export const getStageIndex = (status: ProductionStatus): number =>
  productionStages.findIndex((s) => s.key === status)

// ── Team & Ownership ─────────────────────────────────────────────────────────

export type TeamRole = 'pm' | '3d-artist' | 'unreal-dev' | 'qa'

export const teamRoleConfig: Record<
  TeamRole,
  { label: string; shortLabel: string; color: string; bgColor: string }
> = {
  pm: {
    label: 'Project Manager',
    shortLabel: 'PM',
    color: 'text-indigo-700',
    bgColor: 'bg-indigo-50',
  },
  '3d-artist': {
    label: '3D Artist',
    shortLabel: '3D',
    color: 'text-pink-700',
    bgColor: 'bg-pink-50',
  },
  'unreal-dev': {
    label: 'Unreal Developer',
    shortLabel: 'UE',
    color: 'text-sky-700',
    bgColor: 'bg-sky-50',
  },
  qa: {
    label: 'QA Owner',
    shortLabel: 'QA',
    color: 'text-violet-700',
    bgColor: 'bg-violet-50',
  },
}

export type TeamMember = {
  id: string
  name: string
  initials: string
  role: TeamRole
  avatarColor: string
}

export const teamMembers: TeamMember[] = [
  {
    id: 'sarah-chen',
    name: 'Sarah Chen',
    initials: 'SC',
    role: 'pm',
    avatarColor: 'bg-indigo-500',
  },
  {
    id: 'marcus-rivera',
    name: 'Marcus Rivera',
    initials: 'MR',
    role: 'pm',
    avatarColor: 'bg-indigo-600',
  },
  {
    id: 'alex-kim',
    name: 'Alex Kim',
    initials: 'AK',
    role: '3d-artist',
    avatarColor: 'bg-pink-500',
  },
  {
    id: 'jordan-lee',
    name: 'Jordan Lee',
    initials: 'JL',
    role: '3d-artist',
    avatarColor: 'bg-pink-600',
  },
  {
    id: 'priya-patel',
    name: 'Priya Patel',
    initials: 'PP',
    role: 'unreal-dev',
    avatarColor: 'bg-sky-500',
  },
  {
    id: 'chris-thompson',
    name: 'Chris Thompson',
    initials: 'CT',
    role: 'unreal-dev',
    avatarColor: 'bg-sky-600',
  },
  {
    id: 'taylor-morgan',
    name: 'Taylor Morgan',
    initials: 'TM',
    role: 'qa',
    avatarColor: 'bg-violet-500',
  },
  {
    id: 'sam-williams',
    name: 'Sam Williams',
    initials: 'SW',
    role: 'qa',
    avatarColor: 'bg-violet-600',
  },
]

export const getMemberById = (id: string): TeamMember | undefined =>
  teamMembers.find((m) => m.id === id)

// ── Blockers ─────────────────────────────────────────────────────────────────

export type BlockerCategory =
  | 'awaiting-assets'
  | 'spec-unclear'
  | 'dependency'
  | 'technical'
  | 'builder-response'
  | 'resource'
  | 'other'

export const blockerCategoryConfig: Record<
  BlockerCategory,
  { label: string; icon: string }
> = {
  'awaiting-assets': {
    label: 'Awaiting Assets from Builder',
    icon: 'Download',
  },
  'spec-unclear': {
    label: 'Spec Unclear / Needs Clarification',
    icon: 'HelpCircle',
  },
  dependency: {
    label: 'Dependency on Another Option',
    icon: 'Link',
  },
  technical: {
    label: 'Technical Blocker',
    icon: 'AlertTriangle',
  },
  'builder-response': {
    label: 'Awaiting Builder Response',
    icon: 'Clock',
  },
  resource: {
    label: 'Resource Unavailable',
    icon: 'UserX',
  },
  other: {
    label: 'Other',
    icon: 'MoreHorizontal',
  },
}

export type Blocker = {
  category: BlockerCategory
  note?: string
  createdAt: string
}

// ── Production Record ────────────────────────────────────────────────────────

export type StageTransition = {
  from: ProductionStatus
  to: ProductionStatus
  date: string
  user: string
}

export type ProductionRecord = {
  optionId: string
  productionStatus: ProductionStatus
  qaLevel?: 1 | 2 | 3 // QA round — only relevant when status is 'internal-qa'
  assignments: Partial<Record<TeamRole, string>> // role → teamMemberId
  blockers: Blocker[]
  notes: string
  startDate: string | null
  targetDate: string | null
  stageEnteredAt: string // when current stage was entered
  history: StageTransition[]
}

// ── Mock Production Data ─────────────────────────────────────────────────────

export const productionRecords: ProductionRecord[] = [
  {
    optionId: 'OPT-001',
    productionStatus: 'live',
    assignments: {
      pm: 'sarah-chen',
      '3d-artist': 'alex-kim',
      'unreal-dev': 'priya-patel',
      qa: 'taylor-morgan',
    },
    blockers: [],
    notes: 'Launched with initial builder catalog. Performing well.',
    startDate: '2025-09-15',
    targetDate: '2025-12-01',
    stageEnteredAt: '2025-11-28',
    history: [
      {
        from: 'not-started',
        to: 'spec-confirmed',
        date: '2025-09-20',
        user: 'Sarah Chen',
      },
      {
        from: 'spec-confirmed',
        to: 'in-production',
        date: '2025-10-05',
        user: 'Sarah Chen',
      },
      {
        from: 'in-production',
        to: 'internal-qa',
        date: '2025-11-01',
        user: 'Alex Kim',
      },
      {
        from: 'internal-qa',
        to: 'live',
        date: '2025-11-28',
        user: 'Taylor Morgan',
      },
    ],
  },
  {
    optionId: 'OPT-002',
    productionStatus: 'builder-review',
    assignments: {
      pm: 'sarah-chen',
      '3d-artist': 'alex-kim',
      qa: 'taylor-morgan',
    },
    blockers: [],
    notes: 'Sent to Mattamy for soft launch review.',
    startDate: '2025-10-01',
    targetDate: '2026-01-15',
    stageEnteredAt: '2026-01-05',
    history: [
      {
        from: 'not-started',
        to: 'spec-confirmed',
        date: '2025-10-10',
        user: 'Sarah Chen',
      },
      {
        from: 'spec-confirmed',
        to: 'in-production',
        date: '2025-10-25',
        user: 'Sarah Chen',
      },
      {
        from: 'in-production',
        to: 'internal-qa',
        date: '2025-12-15',
        user: 'Alex Kim',
      },
      {
        from: 'internal-qa',
        to: 'builder-review',
        date: '2026-01-05',
        user: 'Taylor Morgan',
      },
    ],
  },
  {
    optionId: 'OPT-003',
    productionStatus: 'internal-qa',
    qaLevel: 2,
    assignments: {
      pm: 'sarah-chen',
      '3d-artist': 'jordan-lee',
      'unreal-dev': 'priya-patel',
      qa: 'taylor-morgan',
    },
    blockers: [],
    notes: 'QA round 2 — first round had minor texture issues, now resolved.',
    startDate: '2025-10-15',
    targetDate: '2026-02-01',
    stageEnteredAt: '2026-01-20',
    history: [
      {
        from: 'not-started',
        to: 'spec-confirmed',
        date: '2025-10-20',
        user: 'Sarah Chen',
      },
      {
        from: 'spec-confirmed',
        to: 'in-production',
        date: '2025-11-10',
        user: 'Jordan Lee',
      },
      {
        from: 'in-production',
        to: 'internal-qa',
        date: '2026-01-20',
        user: 'Jordan Lee',
      },
    ],
  },
  {
    optionId: 'OPT-004',
    productionStatus: 'in-production',
    assignments: {
      pm: 'sarah-chen',
      '3d-artist': 'alex-kim',
      'unreal-dev': 'chris-thompson',
    },
    blockers: [],
    notes: '3D modeling in progress. Multiple wood grain variants.',
    startDate: '2025-11-01',
    targetDate: '2026-03-01',
    stageEnteredAt: '2025-12-20',
    history: [
      {
        from: 'not-started',
        to: 'spec-confirmed',
        date: '2025-11-10',
        user: 'Sarah Chen',
      },
      {
        from: 'spec-confirmed',
        to: 'in-production',
        date: '2025-12-20',
        user: 'Alex Kim',
      },
    ],
  },
  {
    optionId: 'OPT-005',
    productionStatus: 'spec-confirmed',
    assignments: {
      pm: 'sarah-chen',
      '3d-artist': 'jordan-lee',
    },
    blockers: [
      {
        category: 'awaiting-assets',
        note: 'Waiting on high-res texture samples from Mattamy supplier',
        createdAt: '2026-01-10',
      },
    ],
    notes: 'Spec confirmed. Waiting on physical samples for texture scanning.',
    startDate: '2025-12-01',
    targetDate: '2026-04-01',
    stageEnteredAt: '2026-01-05',
    history: [
      {
        from: 'not-started',
        to: 'proposal-sent',
        date: '2025-12-05',
        user: 'Sarah Chen',
      },
      {
        from: 'proposal-sent',
        to: 'spec-confirmed',
        date: '2025-12-20',
        user: 'Sarah Chen',
      },
    ],
  },
  {
    optionId: 'OPT-006',
    productionStatus: 'live',
    assignments: {
      pm: 'sarah-chen',
      '3d-artist': 'alex-kim',
      qa: 'taylor-morgan',
    },
    blockers: [],
    notes: 'Standard option — simple material, launched early.',
    startDate: '2025-09-15',
    targetDate: '2025-11-01',
    stageEnteredAt: '2025-10-28',
    history: [
      {
        from: 'not-started',
        to: 'in-production',
        date: '2025-09-20',
        user: 'Alex Kim',
      },
      {
        from: 'in-production',
        to: 'live',
        date: '2025-10-28',
        user: 'Taylor Morgan',
      },
    ],
  },
  {
    optionId: 'OPT-007',
    productionStatus: 'in-production',
    assignments: {
      pm: 'marcus-rivera',
      '3d-artist': 'jordan-lee',
      'unreal-dev': 'chris-thompson',
    },
    blockers: [],
    notes:
      'Rendering underway. Similar to OPT-001 but different color profile.',
    startDate: '2025-11-15',
    targetDate: '2026-03-15',
    stageEnteredAt: '2026-01-10',
    history: [
      {
        from: 'not-started',
        to: 'spec-confirmed',
        date: '2025-11-25',
        user: 'Marcus Rivera',
      },
      {
        from: 'spec-confirmed',
        to: 'in-production',
        date: '2026-01-10',
        user: 'Jordan Lee',
      },
    ],
  },
  {
    optionId: 'OPT-008',
    productionStatus: 'in-production',
    assignments: {
      pm: 'marcus-rivera',
      '3d-artist': 'alex-kim',
    },
    blockers: [],
    notes: 'Approved and queued for 3D modeling.',
    startDate: '2025-12-01',
    targetDate: '2026-04-15',
    stageEnteredAt: '2026-01-15',
    history: [
      {
        from: 'not-started',
        to: 'proposal-sent',
        date: '2025-12-05',
        user: 'Marcus Rivera',
      },
      {
        from: 'proposal-sent',
        to: 'spec-confirmed',
        date: '2025-12-18',
        user: 'Marcus Rivera',
      },
      {
        from: 'spec-confirmed',
        to: 'in-production',
        date: '2026-01-15',
        user: 'Marcus Rivera',
      },
    ],
  },
  {
    optionId: 'OPT-009',
    productionStatus: 'spec-confirmed',
    assignments: {
      pm: 'marcus-rivera',
    },
    blockers: [],
    notes: 'Spec locked. Will request assets next week.',
    startDate: '2026-01-05',
    targetDate: '2026-05-01',
    stageEnteredAt: '2026-01-25',
    history: [
      {
        from: 'not-started',
        to: 'proposal-sent',
        date: '2026-01-10',
        user: 'Marcus Rivera',
      },
      {
        from: 'proposal-sent',
        to: 'spec-confirmed',
        date: '2026-01-25',
        user: 'Marcus Rivera',
      },
    ],
  },
  {
    optionId: 'OPT-010',
    productionStatus: 'proposal-sent',
    assignments: {
      pm: 'marcus-rivera',
    },
    blockers: [
      {
        category: 'builder-response',
        note: 'Awaiting Excel confirmation on granite supplier specs',
        createdAt: '2026-01-20',
      },
    ],
    notes: 'Proposal sent to Excel. Waiting on supplier confirmation.',
    startDate: '2026-01-10',
    targetDate: '2026-06-01',
    stageEnteredAt: '2026-01-18',
    history: [
      {
        from: 'not-started',
        to: 'proposal-sent',
        date: '2026-01-18',
        user: 'Marcus Rivera',
      },
    ],
  },
  {
    optionId: 'OPT-011',
    productionStatus: 'integrated',
    assignments: {
      pm: 'sarah-chen',
      '3d-artist': 'jordan-lee',
      'unreal-dev': 'priya-patel',
      qa: 'sam-williams',
    },
    blockers: [],
    notes: 'Integrated into Unreal scene. Ready for QA pass.',
    startDate: '2025-10-01',
    targetDate: '2026-02-15',
    stageEnteredAt: '2026-01-28',
    history: [
      {
        from: 'not-started',
        to: 'spec-confirmed',
        date: '2025-10-15',
        user: 'Sarah Chen',
      },
      {
        from: 'spec-confirmed',
        to: 'in-production',
        date: '2025-11-20',
        user: 'Jordan Lee',
      },
      {
        from: 'in-production',
        to: 'integrated',
        date: '2026-01-28',
        user: 'Priya Patel',
      },
    ],
  },
  {
    optionId: 'OPT-012',
    productionStatus: 'builder-feedback',
    assignments: {
      pm: 'sarah-chen',
      '3d-artist': 'alex-kim',
      'unreal-dev': 'priya-patel',
      qa: 'sam-williams',
    },
    blockers: [
      {
        category: 'spec-unclear',
        note: 'Schell wants panel-ready fronts to match a specific finish — need updated reference photos',
        createdAt: '2026-02-01',
      },
    ],
    notes:
      'Builder reviewed and sent back with feedback on panel finish accuracy.',
    startDate: '2025-09-20',
    targetDate: '2026-02-28',
    stageEnteredAt: '2026-02-01',
    history: [
      {
        from: 'not-started',
        to: 'in-production',
        date: '2025-10-01',
        user: 'Alex Kim',
      },
      {
        from: 'in-production',
        to: 'internal-qa',
        date: '2025-12-15',
        user: 'Alex Kim',
      },
      {
        from: 'internal-qa',
        to: 'builder-review',
        date: '2026-01-10',
        user: 'Sam Williams',
      },
      {
        from: 'builder-review',
        to: 'builder-feedback',
        date: '2026-02-01',
        user: 'Sarah Chen',
      },
    ],
  },
  {
    optionId: 'OPT-013',
    productionStatus: 'not-started',
    assignments: {
      pm: 'sarah-chen',
    },
    blockers: [],
    notes: 'Luxury option — will begin once Cedar Shake is through QA.',
    startDate: null,
    targetDate: '2026-06-01',
    stageEnteredAt: '2025-12-01',
    history: [],
  },
  {
    optionId: 'OPT-014',
    productionStatus: 'in-production',
    assignments: {
      pm: 'marcus-rivera',
      '3d-artist': 'jordan-lee',
      'unreal-dev': 'chris-thompson',
    },
    blockers: [
      {
        category: 'technical',
        note: 'Scratch-resistant coating requires custom shader work in Unreal',
        createdAt: '2026-01-25',
      },
    ],
    notes:
      'Complex material — custom shader needed for scratch-resistant finish.',
    startDate: '2025-11-01',
    targetDate: '2026-04-01',
    stageEnteredAt: '2026-01-05',
    history: [
      {
        from: 'not-started',
        to: 'spec-confirmed',
        date: '2025-11-15',
        user: 'Marcus Rivera',
      },
      {
        from: 'spec-confirmed',
        to: 'in-production',
        date: '2026-01-05',
        user: 'Jordan Lee',
      },
    ],
  },
  {
    optionId: 'OPT-015',
    productionStatus: 'not-started',
    assignments: {},
    blockers: [],
    notes: 'Discontinued by manufacturer. No production planned.',
    startDate: null,
    targetDate: null,
    stageEnteredAt: '2025-09-01',
    history: [],
  },
  {
    optionId: 'OPT-016',
    productionStatus: 'live',
    assignments: {
      pm: 'marcus-rivera',
      '3d-artist': 'alex-kim',
      qa: 'sam-williams',
    },
    blockers: [],
    notes: 'Live and performing well across all Brookfield communities.',
    startDate: '2025-08-01',
    targetDate: '2025-12-01',
    stageEnteredAt: '2025-11-20',
    history: [
      {
        from: 'not-started',
        to: 'in-production',
        date: '2025-08-15',
        user: 'Alex Kim',
      },
      {
        from: 'in-production',
        to: 'internal-qa',
        date: '2025-10-20',
        user: 'Alex Kim',
      },
      {
        from: 'internal-qa',
        to: 'live',
        date: '2025-11-20',
        user: 'Sam Williams',
      },
    ],
  },
  {
    optionId: 'OPT-017',
    productionStatus: 'proposal-sent',
    assignments: {
      pm: 'marcus-rivera',
    },
    blockers: [
      {
        category: 'dependency',
        note: 'Depends on smart home wiring spec from Pulte engineering team',
        createdAt: '2026-01-28',
      },
    ],
    notes:
      'Draft option — proposal sent but depends on Pulte engineering specs.',
    startDate: '2026-01-15',
    targetDate: '2026-07-01',
    stageEnteredAt: '2026-01-22',
    history: [
      {
        from: 'not-started',
        to: 'proposal-sent',
        date: '2026-01-22',
        user: 'Marcus Rivera',
      },
    ],
  },
  {
    optionId: 'OPT-018',
    productionStatus: 'spec-confirmed',
    assignments: {
      pm: 'marcus-rivera',
      '3d-artist': 'jordan-lee',
    },
    blockers: [
      {
        category: 'awaiting-assets',
        note: 'Need panel dimensions and mounting hardware specs from solar supplier',
        createdAt: '2026-02-05',
      },
    ],
    notes:
      'Spec confirmed. Awaiting technical drawings from solar panel supplier.',
    startDate: '2025-12-15',
    targetDate: '2026-05-15',
    stageEnteredAt: '2026-02-01',
    history: [
      {
        from: 'not-started',
        to: 'spec-confirmed',
        date: '2026-01-05',
        user: 'Marcus Rivera',
      },
    ],
  },
  {
    optionId: 'OPT-019',
    productionStatus: 'live',
    assignments: {
      pm: 'sarah-chen',
      '3d-artist': 'alex-kim',
      'unreal-dev': 'priya-patel',
      qa: 'taylor-morgan',
    },
    blockers: [],
    notes: 'The Lawson model — fully live across 3 communities.',
    startDate: '2025-07-01',
    targetDate: '2025-11-01',
    stageEnteredAt: '2025-10-25',
    history: [
      {
        from: 'not-started',
        to: 'in-production',
        date: '2025-07-15',
        user: 'Alex Kim',
      },
      {
        from: 'in-production',
        to: 'integrated',
        date: '2025-09-01',
        user: 'Priya Patel',
      },
      {
        from: 'integrated',
        to: 'internal-qa',
        date: '2025-09-15',
        user: 'Taylor Morgan',
      },
      {
        from: 'internal-qa',
        to: 'live',
        date: '2025-10-25',
        user: 'Taylor Morgan',
      },
    ],
  },
  {
    optionId: 'OPT-020',
    productionStatus: 'internal-qa',
    assignments: {
      pm: 'marcus-rivera',
      '3d-artist': 'jordan-lee',
      'unreal-dev': 'chris-thompson',
      qa: 'sam-williams',
    },
    blockers: [],
    notes: 'The Brookstone model — in QA round 1.',
    startDate: '2025-09-01',
    targetDate: '2026-02-15',
    stageEnteredAt: '2026-01-30',
    history: [
      {
        from: 'not-started',
        to: 'in-production',
        date: '2025-09-15',
        user: 'Jordan Lee',
      },
      {
        from: 'in-production',
        to: 'integrated',
        date: '2025-12-01',
        user: 'Chris Thompson',
      },
      {
        from: 'integrated',
        to: 'internal-qa',
        date: '2026-01-30',
        user: 'Sam Williams',
      },
    ],
  },
  {
    optionId: 'OPT-021',
    productionStatus: 'in-production',
    assignments: {
      pm: 'sarah-chen',
      '3d-artist': 'alex-kim',
      'unreal-dev': 'priya-patel',
    },
    blockers: [],
    notes: 'The Summit luxury model — complex build with premium finishes.',
    startDate: '2025-10-15',
    targetDate: '2026-04-01',
    stageEnteredAt: '2025-12-15',
    history: [
      {
        from: 'not-started',
        to: 'spec-confirmed',
        date: '2025-10-25',
        user: 'Sarah Chen',
      },
      {
        from: 'spec-confirmed',
        to: 'in-production',
        date: '2025-12-15',
        user: 'Alex Kim',
      },
    ],
  },
  // ── Landmark Homes (100% complete) ──
  {
    optionId: 'OPT-022',
    productionStatus: 'live' as ProductionStatus,
    assignments: {
      pm: 'sarah-chen',
      '3d-artist': 'alex-kim',
      'unreal-dev': 'priya-patel',
      qa: 'taylor-morgan',
    },
    blockers: [],
    notes: 'Exterior elevations launched. All color palettes live.',
    startDate: '2025-06-01',
    targetDate: '2025-09-15',
    stageEnteredAt: '2025-09-10',
    history: [
      {
        from: 'not-started' as ProductionStatus,
        to: 'spec-confirmed' as ProductionStatus,
        date: '2025-06-10',
        user: 'Sarah Chen',
      },
      {
        from: 'spec-confirmed' as ProductionStatus,
        to: 'in-production' as ProductionStatus,
        date: '2025-07-05',
        user: 'Alex Kim',
      },
      {
        from: 'in-production' as ProductionStatus,
        to: 'internal-qa' as ProductionStatus,
        date: '2025-08-20',
        user: 'Alex Kim',
      },
      {
        from: 'internal-qa' as ProductionStatus,
        to: 'live' as ProductionStatus,
        date: '2025-09-10',
        user: 'Taylor Morgan',
      },
    ],
  },
  {
    optionId: 'OPT-023',
    productionStatus: 'live' as ProductionStatus,
    assignments: {
      pm: 'sarah-chen',
      '3d-artist': 'jordan-lee',
      'unreal-dev': 'priya-patel',
      qa: 'taylor-morgan',
    },
    blockers: [],
    notes: 'Interior flooring options complete. All variants rendered.',
    startDate: '2025-06-15',
    targetDate: '2025-10-01',
    stageEnteredAt: '2025-09-25',
    history: [
      {
        from: 'not-started' as ProductionStatus,
        to: 'spec-confirmed' as ProductionStatus,
        date: '2025-06-22',
        user: 'Sarah Chen',
      },
      {
        from: 'spec-confirmed' as ProductionStatus,
        to: 'in-production' as ProductionStatus,
        date: '2025-07-20',
        user: 'Jordan Lee',
      },
      {
        from: 'in-production' as ProductionStatus,
        to: 'internal-qa' as ProductionStatus,
        date: '2025-09-01',
        user: 'Jordan Lee',
      },
      {
        from: 'internal-qa' as ProductionStatus,
        to: 'live' as ProductionStatus,
        date: '2025-09-25',
        user: 'Taylor Morgan',
      },
    ],
  },
  {
    optionId: 'OPT-024',
    productionStatus: 'live' as ProductionStatus,
    assignments: {
      pm: 'sarah-chen',
      '3d-artist': 'alex-kim',
      'unreal-dev': 'chris-thompson',
      qa: 'sam-williams',
    },
    blockers: [],
    notes: 'Kitchen cabinetry package complete. Premium finishes included.',
    startDate: '2025-07-01',
    targetDate: '2025-10-15',
    stageEnteredAt: '2025-10-08',
    history: [
      {
        from: 'not-started' as ProductionStatus,
        to: 'spec-confirmed' as ProductionStatus,
        date: '2025-07-10',
        user: 'Sarah Chen',
      },
      {
        from: 'spec-confirmed' as ProductionStatus,
        to: 'in-production' as ProductionStatus,
        date: '2025-08-05',
        user: 'Alex Kim',
      },
      {
        from: 'in-production' as ProductionStatus,
        to: 'internal-qa' as ProductionStatus,
        date: '2025-09-15',
        user: 'Alex Kim',
      },
      {
        from: 'internal-qa' as ProductionStatus,
        to: 'live' as ProductionStatus,
        date: '2025-10-08',
        user: 'Sam Williams',
      },
    ],
  },
  {
    optionId: 'OPT-025',
    productionStatus: 'live' as ProductionStatus,
    assignments: {
      pm: 'jordan-lee',
      '3d-artist': 'alex-kim',
      'unreal-dev': 'priya-patel',
      qa: 'taylor-morgan',
    },
    blockers: [],
    notes: 'The Ridgewood model fully launched. All walkthroughs active.',
    startDate: '2025-05-15',
    targetDate: '2025-09-01',
    stageEnteredAt: '2025-08-28',
    history: [
      {
        from: 'not-started' as ProductionStatus,
        to: 'spec-confirmed' as ProductionStatus,
        date: '2025-05-25',
        user: 'Jordan Lee',
      },
      {
        from: 'spec-confirmed' as ProductionStatus,
        to: 'in-production' as ProductionStatus,
        date: '2025-06-20',
        user: 'Alex Kim',
      },
      {
        from: 'in-production' as ProductionStatus,
        to: 'internal-qa' as ProductionStatus,
        date: '2025-08-10',
        user: 'Alex Kim',
      },
      {
        from: 'internal-qa' as ProductionStatus,
        to: 'live' as ProductionStatus,
        date: '2025-08-28',
        user: 'Taylor Morgan',
      },
    ],
  },
]

// ── Generated Additional Records (scale up to ~20-25 per builder) ────────────

const generatedStatuses: Record<string, ProductionStatus[]> = {
  landmark: Array(20).fill('live'),
  mattamy: [
    'live',
    'live',
    'live',
    'live',
    'live',
    'live',
    'live',
    'live',
    'builder-review',
    'internal-qa',
    'internal-qa',
    'integrated',
    'in-production',
    'in-production',
    'in-production',
    'spec-confirmed',
    'proposal-sent',
    'not-started',
  ],
  schell: [
    'live',
    'live',
    'live',
    'live',
    'builder-feedback',
    'internal-qa',
    'internal-qa',
    'integrated',
    'in-production',
    'in-production',
    'in-production',
    'in-production',
    'in-production',
    'spec-confirmed',
    'spec-confirmed',
    'spec-confirmed',
    'proposal-sent',
    'not-started',
    'not-started',
  ],
  excel: [
    'live',
    'live',
    'live',
    'builder-review',
    'internal-qa',
    'integrated',
    'in-production',
    'in-production',
    'in-production',
    'in-production',
    'spec-confirmed',
    'spec-confirmed',
    'spec-confirmed',
    'proposal-sent',
    'proposal-sent',
    'not-started',
    'not-started',
    'not-started',
  ],
  brookfield: [
    'live',
    'live',
    'live',
    'live',
    'live',
    'internal-qa',
    'internal-qa',
    'integrated',
    'in-production',
    'in-production',
    'in-production',
    'in-production',
    'spec-confirmed',
    'spec-confirmed',
    'proposal-sent',
    'not-started',
    'not-started',
    'not-started',
    'not-started',
  ],
  pulte: [
    'live',
    'live',
    'internal-qa',
    'in-production',
    'in-production',
    'in-production',
    'in-production',
    'spec-confirmed',
    'spec-confirmed',
    'proposal-sent',
    'proposal-sent',
    'not-started',
    'not-started',
    'not-started',
    'not-started',
    'not-started',
    'not-started',
    'not-started',
    'not-started',
  ],
}

const blockedIndices: Record<string, number[]> = {
  landmark: [],
  mattamy: [8, 14],
  schell: [4, 11, 16],
  excel: [3, 9],
  brookfield: [7],
  pulte: [2, 6],
}

const pmPool = ['sarah-chen', 'jordan-lee']
const artistPool = ['alex-kim', 'jordan-lee']
const devPool = ['priya-patel', 'chris-thompson']
const qaPool = ['taylor-morgan', 'sam-williams']

const blockerTemplates: Blocker[] = [
  {
    id: 'gen-b1',
    category: 'awaiting-assets',
    description: 'Waiting on updated material specs from builder',
    severity: 'medium',
    createdAt: '2026-02-15',
    resolvedAt: null,
  },
  {
    id: 'gen-b2',
    category: 'spec-unclear',
    description: 'Elevation drawings need clarification',
    severity: 'high',
    createdAt: '2026-02-20',
    resolvedAt: null,
  },
  {
    id: 'gen-b3',
    category: 'builder-response',
    description: 'Pending builder approval on color selections',
    severity: 'low',
    createdAt: '2026-03-01',
    resolvedAt: null,
  },
  {
    id: 'gen-b4',
    category: 'technical',
    description: 'Mesh optimization needed for performance',
    severity: 'medium',
    createdAt: '2026-02-28',
    resolvedAt: null,
  },
  {
    id: 'gen-b5',
    category: 'dependency',
    description: 'Blocked by upstream floor plan changes',
    severity: 'high',
    createdAt: '2026-03-05',
    resolvedAt: null,
  },
]

const builderIds = [
  'landmark',
  'mattamy',
  'schell',
  'excel',
  'brookfield',
  'pulte',
]

let genIdx = 100
builderIds.forEach((bid) => {
  const statuses = generatedStatuses[bid] || []
  const blocked = blockedIndices[bid] || []
  statuses.forEach((status, i) => {
    const optionId = `OPT-${genIdx}`
    genIdx++
    const isBlocked =
      blocked.includes(i) && status !== 'live' && status !== 'not-started'
    const startMonth = 3 + Math.floor(i / 4)
    const startDate = `2025-${String(Math.min(startMonth, 12)).padStart(2, '0')}-${String(5 + (i % 20)).padStart(2, '0')}`
    const enteredMonth = Math.min(startMonth + 2 + Math.floor(i / 6), 12)
    const stageEnteredAt =
      status === 'not-started'
        ? startDate
        : `2025-${String(enteredMonth).padStart(2, '0')}-${String(10 + (i % 15)).padStart(2, '0')}`

    productionRecords.push({
      optionId,
      productionStatus: status,
      assignments: {
        pm: pmPool[i % pmPool.length],
        '3d-artist': artistPool[i % artistPool.length],
        'unreal-dev': devPool[i % devPool.length],
        ...(status !== 'not-started' && status !== 'proposal-sent'
          ? { qa: qaPool[i % qaPool.length] }
          : {}),
      },
      blockers: isBlocked
        ? [
            {
              ...blockerTemplates[i % blockerTemplates.length],
              id: `gen-b-${optionId}`,
            },
          ]
        : [],
      notes: '',
      startDate: status === 'not-started' ? null : startDate,
      targetDate: `2026-${String(Math.min(startMonth + 4, 12)).padStart(2, '0')}-01`,
      stageEnteredAt,
      history: [],
    })
  })
})

export const getProductionRecord = (
  optionId: string,
): ProductionRecord | undefined =>
  productionRecords.find((r) => r.optionId === optionId)

// ── Builder-level Production Summary Helper ──────────────────────────────────

export type BuilderProductionSummary = {
  totalOptions: number
  live: number
  inProgress: number
  blocked: number
  notStarted: number
  percentComplete: number
  byStage: Record<ProductionStatus, number>
}

export const getBuilderProductionSummary = (
  optionIds: string[],
): BuilderProductionSummary => {
  const records = productionRecords.filter((r) =>
    optionIds.includes(r.optionId),
  )
  const byStage = {} as Record<ProductionStatus, number>
  productionStages.forEach((s) => (byStage[s.key] = 0))
  records.forEach((r) => {
    byStage[r.productionStatus] = (byStage[r.productionStatus] || 0) + 1
  })

  const total = records.length
  const live = byStage['live'] || 0
  const notStarted = byStage['not-started'] || 0
  const blocked = records.filter((r) => r.blockers.length > 0).length
  const inProgress = total - live - notStarted

  return {
    totalOptions: total,
    live,
    inProgress,
    blocked,
    notStarted,
    percentComplete: total > 0 ? Math.round((live / total) * 100) : 0,
    byStage,
  }
}
